import { Injectable } from "@angular/core";
import { NewsViewModel, NewsDataModel } from "../../shared/models/news.model";
import { ContentType, ImageOrientation, DateFormatTyps } from "../../shared/contentTypes";
import { RatingService } from "../../services/rating.service";
import { LikeService } from "../../services/like.service";
import { CoreLogService } from "../../services/log.service";
import { Observable } from "rxjs";
import DateManager from "../../shared/dateManager";
import { CircularInjectionHelper } from "../../services/circularInjectionHelper";
import { UserHelperService } from "../../services/userHelper.service";
import { IObjectObserver } from "../../services/IObjectObserver";
import { ObjectObserverFactory } from "../../services/objectObserverFactory";

@Injectable()
export class NewsService {
  private _newsObsServ: IObjectObserver<NewsDataModel>;

  constructor(private _coreLogService: CoreLogService,
    private _ratingService: RatingService,
    private _likeService: LikeService,
    private _userHelperService: UserHelperService,
    private objObsInjectHelper: CircularInjectionHelper) {
    this._newsObsServ = ObjectObserverFactory.GetObjectObserverByInjectionHelper<NewsDataModel>(ContentType.News, objObsInjectHelper);
  }

  public get ObserveChanges(): Observable<(string | number)[]> {
    return this._newsObsServ.ObserveChanges;
  }

  /**
   * Getting the list of newsDataModel
   * Create newsViewModelList
   * @result return a promise that resolves a list of newsViewModel
   */
  public GetNewsViewModel(): Promise<NewsViewModel[]> {
    const me = this;
    let isTestUser: boolean = false;
    return new Promise<NewsViewModel[]>((resolve, reject) => {
      isTestUser = me._userHelperService.IsTestUser;
      me._newsObsServ.GetObjectsByFilter(model => !model.deleted && model.release && (model.public || isTestUser))
        .then((news) => {
          me.mapDataModelListToViewModelList(news)
            .then(resolve)
            .catch(e => {
              me.objObsInjectHelper.coreLogService.error("NewsService.GetNewsViewModel", "GetObjectsByFilter.mapDataModelListToViewModelList", e);
              reject();
            });
        })
        .catch((e) => {
          me.objObsInjectHelper.coreLogService.error("NewsService.GetNewsViewModel", "GetObjectsByFilter", e);
          reject();
        });
    });
  }

  // Mapper

  /**
  * map a newsDataModel to newsViewModel
  * @param model Get a newsDataModel for mapping
  * @result return a newsViewModel
  */
  private async mapDataModelToViewModel(model: NewsDataModel): Promise<NewsViewModel> {
    const me = this;
    const viewModel = new NewsViewModel();
    viewModel.id = model.id;
    viewModel.dateYMD = model.date_ymd;
    viewModel.date = DateManager.MomentAsString(model.date, DateFormatTyps.DDMMYYYY);
    viewModel.day = viewModel.date.slice(0, 3);
    viewModel.month = viewModel.date.slice(3, 6);
    viewModel.year = viewModel.date.slice(6);
    viewModel.yearLastDig = viewModel.date.slice(8); //Last two Digits of the year
    viewModel.momentDate = model.date;
    viewModel.label_de = model.label_de;
    viewModel.label_en = model.label_en;
    viewModel.text_de = model.text_de.replace("\n","<br>");
    viewModel.text_en = model.text_en.replace("\n","<br>");;
    viewModel.image_photo = ContentType.News + "/" + model.id + "_photo_" + ImageOrientation.Landscape + "/" + model.last_image_update_at_ymd + "/" + model.last_image_update_at_hms;
    viewModel.locations = model.locations;

    try {
      viewModel.like = await me._likeService.HasLike(ContentType.News, model.id);
    } catch (error) {
      me._coreLogService.error("NewsService.mapDataModelToViewModel", "HasLike try catch", error);
    }

    return Promise.resolve(viewModel);
  }

  /**
  * map a list of newsDataModel to a list of newsViewModel
  * @param model Get a list of newsDataModel for mapping
  * @result return a list of newsViewModel
  */
  private mapDataModelListToViewModelList(modelList: NewsDataModel[]): Promise<NewsViewModel[]> {
    const me = this;
    if (modelList && modelList.length > 0) {
      return Promise.all(modelList.map((model) => me.mapDataModelToViewModel(model)));
    } else {
      return Promise.resolve(null);
    }
  }
}
